<template>
  <div
    ref="container"
    class="report-graph echarts-preview tw-w-full"
    :style="{ height: props.height }"
  />
</template>

<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import type {
  GraphData,
  SeriesTarget,
} from '@app/application/report/screens/graphs-echarts/types'
import {
  isSeriesTargetClickable,
  lookupSeriesTarget,
} from '@app/application/report/screens/graphs-echarts/utils/series-target'
import { useEchartsChart } from '@app/shared/echarts/use-echarts-chart'

const props = withDefaults(
  defineProps<{
    graphData: GraphData | null
    height?: string
    hideXAxisLabels?: boolean
    compactYAxis?: boolean
  }>(),
  {
    height: '100%',
    hideXAxisLabels: false,
    compactYAxis: false,
  }
)

const emit =
  defineEmits<{
    'series-click': [target: SeriesTarget]
  }>()

const container = ref<HTMLElement | null>(null)
const graphDataRef = toRef(props, 'graphData')
const optionsRef = computed(() => ({
  hideXAxisLabels: props.hideXAxisLabels,
  compactYAxis: props.compactYAxis,
}))

// Resolves an echarts click event to the report rows the underlying graph
// metric targets (referenced report elements and accounts). Returns an empty
// target for a piechart "Other" slice, the waterfallchart placeholder bars, or
// anything else the metric's formula doesn't reference.
// - Linechart: resolved via seriesName.
// - Piechart:  slice name carries a " (NN.NN%)" suffix (see
//   GraphLogic#adjusted_columns); strip it before lookup.
// - Waterfallchart: resolved via params.name (the category).
function resolveTarget(
  params: { seriesName?: string; name?: string },
  graphData: GraphData
): SeriesTarget {
  const candidate =
    graphData.graphType === 'linechart' ? params.seriesName : params.name
  return lookupSeriesTarget(graphData.seriesTargets, candidate)
}

function handleClick(params: { seriesName?: string; name?: string }): void {
  if (!props.graphData) return
  const target = resolveTarget(params, props.graphData)
  if (isSeriesTargetClickable(target)) emit('series-click', target)
}

useEchartsChart(container, graphDataRef, optionsRef, handleClick)
</script>
